import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { PaperAirplaneIcon, StopIcon, TrashIcon } from '@heroicons/react/24/outline';
import { CatalogEntry, PriceRow } from '../../services/inferenceApi';
import { CATALOG_CATEGORIES, formatPrice } from '../../utils/catalog';
import { ChatSession, useChatSession } from '../../hooks/useChatSession';
import MessageList from './MessageList';

interface CompareChatProps {
  /** Chat-capable models only (media models can't stream chat). */
  models: CatalogEntry[];
  currency: string;
  modelA: string;
  modelB: string;
  onModelsChange: (a: string, b: string) => void;
  temperature: number;
  maxTokens: number;
  apiKeyOverride: string | null;
  keyMissing: boolean;
  priceByName: Map<string, PriceRow>;
}

function ModelSelect({
  id,
  label,
  value,
  models,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  models: CatalogEntry[];
  onChange: (v: string) => void;
}) {
  const groups = useMemo(() => {
    const known = CATALOG_CATEGORIES.map((c) => ({
      key: c.key,
      label: c.label,
      items: models.filter((m) => m.category === c.key),
    }));
    const other = models.filter((m) => !CATALOG_CATEGORIES.some((c) => c.key === m.category));
    return [...known, { key: 'other', label: 'Other', items: other }].filter((g) => g.items.length > 0);
  }, [models]);

  return (
    <>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <select id={id} className="input min-w-0 flex-1" value={value} onChange={(e) => onChange(e.target.value)}>
        {!value && <option value="">Choose a model…</option>}
        {groups.map((g) => (
          <optgroup key={g.key} label={g.label}>
            {g.items.map((m) => (
              <option key={m.id} value={m.display_name}>
                {m.display_name}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </>
  );
}

function Pane({
  side,
  session,
  model,
  models,
  currency,
  onModelChange,
  retryTokens,
}: {
  side: 'A' | 'B';
  session: ChatSession;
  model: string;
  models: CatalogEntry[];
  currency: string;
  onModelChange: (v: string) => void;
  retryTokens: number;
}) {
  const entry = models.find((m) => m.display_name === model);
  const price = entry ? formatPrice(entry, currency) : null;

  return (
    <section aria-label={`Model ${side} conversation`} className="ps-card flex min-w-0 flex-col">
      <div className="flex flex-col gap-2 border-b border-ps-border p-3.5">
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="grid h-7 w-7 flex-none place-items-center rounded-md bg-ps-accent-soft text-xs font-semibold text-ps-accent"
          >
            {side}
          </span>
          <ModelSelect
            id={`compare-model-${side.toLowerCase()}`}
            label={`Model ${side}`}
            value={model}
            models={models}
            onChange={onModelChange}
          />
          {session.busy && (
            <button type="button" className="ps-btn2 !min-h-[36px] !px-3" onClick={session.stop}>
              <StopIcon className="h-4 w-4" />
              <span className="sr-only">Stop model {side}</span>
            </button>
          )}
        </div>
        {entry && (entry.context_length > 0 || price) && (
          <div className="flex flex-wrap justify-between gap-2 text-xs text-ps-subtle">
            {entry.context_length > 0 && <span>{entry.context_length.toLocaleString()} context</span>}
            {price && <span>{price.output ? `${price.input} · ${price.output}` : price.input}</span>}
          </div>
        )}
      </div>
      <div
        className="flex max-h-[calc(100vh-420px)] min-h-[360px] flex-1 flex-col gap-4 overflow-y-auto p-4"
        aria-live="polite"
        aria-busy={session.busy}
      >
        {session.messages.length === 0 ? (
          <p className="m-auto max-w-[260px] text-center text-[13px] leading-relaxed text-ps-muted">
            Replies from model {side} show up here, with tokens, cost and speed.
          </p>
        ) : (
          <MessageList
            session={session}
            retryTokens={retryTokens}
            onRetry={() => session.retryTruncated(retryTokens)}
            showTiming
            compact
          />
        )}
      </div>
    </section>
  );
}

/**
 * Side-by-side model comparison: two independent conversations sharing the
 * Playground's temperature, max tokens and auth. One prompt goes to both
 * models, or each model gets its own; either way both requests stream in
 * parallel.
 */
export default function CompareChat({
  models,
  currency,
  modelA,
  modelB,
  onModelsChange,
  temperature,
  maxTokens,
  apiKeyOverride,
  keyMissing,
  priceByName,
}: CompareChatProps) {
  const common = { temperature, maxTokens, apiKeyOverride, priceByName };
  const a = useChatSession({ ...common, model: modelA, label: modelA || 'Model A' });
  const b = useChatSession({ ...common, model: modelB, label: modelB || 'Model B' });

  const [separate, setSeparate] = useState(false);
  const [shared, setShared] = useState('');
  const [promptA, setPromptA] = useState('');
  const [promptB, setPromptB] = useState('');

  // Default to the first two chat models once the catalog loads.
  useEffect(() => {
    if (models.length === 0) return;
    const has = (n: string) => models.some((m) => m.display_name === n);
    const nextA = has(modelA) ? modelA : models[0].display_name;
    const nextB = has(modelB) ? modelB : (models.find((m) => m.display_name !== nextA) ?? models[0]).display_name;
    if (nextA !== modelA || nextB !== modelB) onModelsChange(nextA, nextB);
  }, [models, modelA, modelB, onModelsChange]);

  const anyBusy = a.busy || b.busy;
  const textA = separate ? promptA : shared;
  const textB = separate ? promptB : shared;
  const canSend =
    !anyBusy && !keyMissing && Boolean(modelA && modelB) && (textA.trim() !== '' || textB.trim() !== '');
  const retryTokens = Math.max(4096, maxTokens * 2);

  const sendBoth = () => {
    if (!canSend) return;
    // Both sends start before either awaits, so the two requests run in parallel.
    const sentA = textA.trim() ? a.send(textA) : false;
    const sentB = textB.trim() ? b.send(textB) : false;
    if (separate) {
      if (sentA) setPromptA('');
      if (sentB) setPromptB('');
    } else if (sentA || sentB) {
      setShared('');
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendBoth();
    }
  };

  const clearBoth = () => {
    a.clear();
    b.clear();
  };

  return (
    <div className="flex min-w-0 flex-[3_1_520px] flex-col gap-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <Pane
          side="A"
          session={a}
          model={modelA}
          models={models}
          currency={currency}
          onModelChange={(v) => onModelsChange(v, modelB)}
          retryTokens={retryTokens}
        />
        <Pane
          side="B"
          session={b}
          model={modelB}
          models={models}
          currency={currency}
          onModelChange={(v) => onModelsChange(modelA, v)}
          retryTokens={retryTokens}
        />
      </div>

      <section aria-label="Prompt" className="ps-card flex flex-col gap-3 p-3.5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex min-h-[32px] cursor-pointer items-center gap-2 text-[13px] text-ps-muted">
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary-500"
              checked={separate}
              onChange={(e) => {
                const on = e.target.checked;
                // Carry the typed text over so switching modes never loses it.
                if (on && shared) {
                  setPromptA(shared);
                  setPromptB(shared);
                } else if (!on && !shared) {
                  setShared(promptA || promptB);
                }
                setSeparate(on);
              }}
            />
            Different prompt for each model
          </label>
          <button
            type="button"
            className="ps-btn2 !min-h-[32px]"
            onClick={clearBoth}
            disabled={anyBusy || (a.messages.length === 0 && b.messages.length === 0)}
          >
            <TrashIcon className="h-4 w-4" /> Clear both
          </button>
        </div>

        {separate ? (
          <div className="grid gap-3 lg:grid-cols-2">
            {(
              [
                ['A', modelA, promptA, setPromptA],
                ['B', modelB, promptB, setPromptB],
              ] as const
            ).map(([side, name, value, set]) => (
              <div key={side} className="flex flex-col gap-1.5">
                <label htmlFor={`compare-prompt-${side.toLowerCase()}`} className="text-xs text-ps-muted">
                  Prompt for model {side}
                  {name ? ` · ${name}` : ''}
                </label>
                <textarea
                  id={`compare-prompt-${side.toLowerCase()}`}
                  className="input min-h-[72px] resize-y py-2"
                  rows={3}
                  placeholder={`Message model ${side}…`}
                  value={value}
                  onChange={(e) => set(e.target.value)}
                  onKeyDown={onKeyDown}
                  disabled={anyBusy}
                />
              </div>
            ))}
          </div>
        ) : (
          <div>
            <label htmlFor="compare-prompt" className="sr-only">
              Prompt for both models
            </label>
            <textarea
              id="compare-prompt"
              className="input min-h-[72px] w-full resize-y py-2"
              rows={3}
              placeholder="Message both models…"
              value={shared}
              onChange={(e) => setShared(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={anyBusy}
            />
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-ps-subtle">
            {keyMissing
              ? 'Paste an API key in the settings to enable sending.'
              : 'Enter sends to both in parallel · Shift+Enter for a new line. Each model is billed for its own reply.'}
          </span>
          {anyBusy ? (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                a.stop();
                b.stop();
              }}
            >
              <StopIcon className="h-4 w-4" /> Stop both
            </button>
          ) : (
            <button type="button" className="btn-primary" onClick={sendBoth} disabled={!canSend}>
              <PaperAirplaneIcon className="h-4 w-4" /> {separate ? 'Send both' : 'Send to both'}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
